import React, { useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LoadingSpinner from "../common/loading";
import { useToast } from "../common/toast";
import { useTheme } from "@mui/material/styles";
import useApiCall from "../common/api-call";
import { Chip } from "@mui/material";
import { useHistory } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";

const PremadeSearchView = () => {
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [premades, setPremades] = useState([]);
  const { successToast, errorToast } = useToast();
  const theme = useTheme();
  const { apiCall } = useApiCall();
  const history = useHistory();

  const handleSearch = async () => {
    if (!searchText.trim()) {
      setPremades([]);
      return;
    }

    setLoading(true);
    try {
      const request = { searchText: searchText };
      const response = await apiCall("/premade/search", "POST", request);

      if (response && !response.error) {
        setPremades(response.premades || []);
      } else {
        throw new Error(response?.error || "Unknown error");
      }
    } catch (error) {
      errorToast("Error searching premades! Please retry.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPremade = premadeId => {
    history.push(`/premade/script?id=${premadeId}`);
  };

  const renderPremadeContent = premade => {
    return (
      <Box sx={{ mt: 1 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="textSecondary">
            Version:{" "}
            <span style={{ color: theme.palette.primary.main }}>
              {premade.version}
            </span>
          </Typography>
          <Typography variant="subtitle2" color="textSecondary">
            Creator:{" "}
            <span style={{ color: theme.palette.primary.main }}>
              {premade.creator}
            </span>
          </Typography>
        </Box>

        <Typography variant="subtitle2" color="textSecondary">
          Description:
        </Typography>
        <Typography variant="body2" sx={{ ml: 2, mb: 2 }}>
          {premade.description || "No description available"}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <IconButton
            color="primary"
            onClick={() => handleEditPremade(premade.id)}
            size="small"
          >
            <EditIcon />
          </IconButton>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ px: 2, py: 1 }}>
        <Paper
          elevation={0}
          component="form"
          sx={{
            p: "2px 4px",
            display: "flex",
            alignItems: "center",
            width: "100%",
            mb: 1,
          }}
          onSubmit={e => {
            e.preventDefault();
            handleSearch();
          }}
        >
          <InputBase
            sx={{ ml: 1, flex: 1 }}
            placeholder="Search premades..."
            name="search"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onKeyPress={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch();
              }
            }}
          />
          <IconButton sx={{ p: "10px" }} onClick={handleSearch}>
            <SearchIcon />
          </IconButton>
        </Paper>
      </Box>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <Box sx={{ px: 2 }}>
          {premades.map((premade, index) => (
            <Accordion key={index}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
              >
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" component="div">
                      {premade.id}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {premade.summary}
                    </Typography>
                  </Box>
                  <Box>
                    {premade.status && (
                      <Chip
                        label={premade.status}
                        size="small"
                        color={
                          premade.status === "PUBLISHED" ? "success" : "default"
                        }
                        variant="outlined"
                        sx={{ mr: 1 }}
                      />
                    )}
                    {premade.archived && (
                      <Chip
                        label="Archived"
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {renderPremadeContent(premade)}
              </AccordionDetails>
            </Accordion>
          ))}
          {premades.length === 0 && searchText.trim() !== "" && (
            <Paper sx={{ p: 2, textAlign: "center" }}>
              <Typography>No premades found</Typography>
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PremadeSearchView;
